/**
 * The timing vocabulary of a flow: the wait between two steps, the span of
 * the whole sequence, and the sentences the hero and the cards build from
 * them. Day offsets are days since the signup, one decimal.
 */

const DAYS_PER_MONTH = 30
const MONTH_THRESHOLD_DAYS = 60

function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many
}

/** "14 days" / "3 months" / "same day". */
export function formatFlowSpan(spanDays: number): string {
  if (spanDays <= 0) {
    return 'same day'
  }
  if (spanDays >= MONTH_THRESHOLD_DAYS) {
    const months = Math.round(spanDays / DAYS_PER_MONTH)
    return `${months} ${plural(months, 'month', 'months')}`
  }
  return `${spanDays} ${plural(spanDays, 'day', 'days')}`
}

export function formatEmailCount(emailCount: number): string {
  return `${emailCount} ${plural(emailCount, 'email', 'emails')}`
}

const HOURS_PER_DAY = 24

/**
 * The distance between two emails, in whole units: "same day" / "3 hours" /
 * "1 day" / "2 days 12 hours". Day offsets are monotone, so a negative delta
 * reads as the same day.
 *
 * Offsets carry one decimal, so their difference is both fractional and
 * float-noisy (6.2 - 1.1 = 5.100000000000001). Rounding the delta to whole
 * hours first is what keeps that off the stage: every label is a whole
 * number of days and hours, never a decimal.
 */
export function formatGapDistance(
  fromDayOffset: number,
  toDayOffset: number
): string {
  const delta = Math.abs(toDayOffset - fromDayOffset)
  if (delta <= 0) {
    return 'same day'
  }
  // A gap under half an hour still happened: floor it at one hour rather
  // than rounding it away into "same day".
  const totalHours = Math.max(1, Math.round(delta * HOURS_PER_DAY))
  const days = Math.floor(totalHours / HOURS_PER_DAY)
  const hours = totalHours % HOURS_PER_DAY
  if (days === 0) {
    return `${hours} ${plural(hours, 'hour', 'hours')}`
  }
  const dayPart = `${days} ${plural(days, 'day', 'days')}`
  return hours === 0
    ? dayPart
    : `${dayPart} ${hours} ${plural(hours, 'hour', 'hours')}`
}

/**
 * The label over a neighbouring email on the stage, relative to the one in
 * focus: "2 days later" over the next one, "2 days before" over the
 * previous one, "same day" for either.
 */
export function describeStepGap(
  focusDayOffset: number,
  neighbourDayOffset: number,
  direction: 'later' | 'before'
): string {
  const distance = formatGapDistance(focusDayOffset, neighbourDayOffset)
  return distance === 'same day' ? distance : `${distance} ${direction}`
}

/** The card line under the brand: "5 emails in 14 days" / "3 emails, same day". */
export function formatFlowCardSubtitle(args: {
  emailCount: number
  spanDays: number
}): string {
  const emails = formatEmailCount(args.emailCount)
  return args.spanDays > 0
    ? `${emails} in ${formatFlowSpan(args.spanDays)}`
    : `${emails}, same day`
}
